// React
import { useEffect, useState, useRef } from 'react'

// React Router
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'

// Hook
import { useDebouncedValue, useDidUpdate, useSet } from '@mantine/hooks'

// Axios API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Lodash
import { debounce } from 'lodash'

// MD5
import md5 from 'md5'
import { resolveTableColumnAliases } from '@/lib/tableColumnAliases.mjs'
import { orderedTableColumns } from '@/lib/tableColumns.mjs'

// useTable hook
export default function useTable({
    tableRef,
    availableColumns = [],
    availableFilters,
    engineApiEndpoint,
    defaultSortColumn = 'id',
    defaultSortDirection = 'desc',
    selectKey,
}) {
    // Scroll top
    const scrollTop = () => {
        if (tableRef && tableRef.current) {
            tableRef.current.scrollTop = 0
        }
    }

    // Axios API
    const { requestGet, loading } = useAxiosApi()

    // Initialize router
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()

    // Table state
    const [tableState, setTableState] = useState({
        filters: [],
        orderBy: defaultSortColumn,
        orderByDirection: defaultSortDirection,
        page: null,
        perPage: null,
        query: null,
        columns: [],
        location_id: null,
    })

    const tableStateRef = useRef(tableState)

    // Query
    const [query, setQuery] = useState('')
    const [debouncedQuery] = useDebouncedValue(query, 500)

    useDidUpdate(() => {
        updateURLWithNewState({
            ...tableState,
            query,
            page: 1,
        })
    }, [debouncedQuery])

    // Filters
    const changeFilter = (key, value) => {
        const newFilters = {
            ...tableState.filters,
            [key]: value,
        }

        updateURLWithNewState({
            ...tableState,
            filters: newFilters,
            page: 1,
        })
    }

    const changeFilters = (newFilters) => {
        updateURLWithNewState({
            ...tableState,
            filters: newFilters,
            page: 1,
        })
    }

    const resetFilters = () => {
        updateURLWithNewState({
            ...tableState,
            filters: {},
            page: 1,
        })
    }

    // Location id
    const changeLocationId = (location_id) => {
        updateURLWithNewState({
            ...tableState,
            location_id,
            page: 1,
        })
    }

    // Columns
    const columns = orderedTableColumns(availableColumns, tableState.columns, tableState.columnOrder)

    // Fetching data
    const [tableData, setTableData] = useState(null)
    const [count, setCount] = useState(null)
    const [isFetching, setIsFetching] = useState(false)
    const [isFirstFetch, setIsFirstFetch] = useState(true)

    const fetchData = async (tableState, shouldScrollTop = true) => {
        // Don't fetch data if page or perPage is not set
        if (!tableState.page || !tableState.perPage) {
            return
        }

        if (engineApiEndpoint) {
            const params = {
                page: tableState.page,
                per_page: tableState.perPage,
                order_by: tableState.orderBy,
                order_by_direction: tableState.orderByDirection,
                filters: tableState.filters,
                search: tableState.query,
                columns: tableState.columns,
                location_id: tableState.location_id,
            }

            const { data, count } = await requestGet(engineApiEndpoint, params)
            setTableData(data.data)
            setCount(data.total)

            if (shouldScrollTop) {
                scrollTop()
            }

            if (isFirstFetch) {
                setIsFirstFetch(false)
                restoreScroll()
            }
        }
    }

    const refetchData = async (shouldScrollTop = true) => {
        fetchData(tableState, shouldScrollTop)
    }

    const updateURLWithNewState = (newState) => {
        // Update URL
        const query = []
        if (newState.viewId) query.push({ key: 'viewId', value: newState.viewId })

        // Set page and perPage
        if (newState.page && newState.page != 1) {
            query.push({
                key: 'page',
                value: newState.page,
            })
        }

        if (newState.perPage && newState.perPage != 100) {
            query.push({
                key: 'perPage',
                value: newState.perPage,
            })
        }

        // Set filters for URL (comma separated if array)
        for (const key in newState.filters) {
            const filterProps = availableFilters.find(
                (filter) => filter.key === key
            )

            // If filter is available
            if (filterProps && filterProps.type) {
                // If filter is of type select or multiselect
                if (filterProps.type === 'multiselect') {
                    if (newState.filters[key].length > 0) {
                        query.push({
                            key,
                            value: newState.filters[key].join(','),
                        })
                    }
                } else if (filterProps.type === 'select') {
                    if (newState.filters[key]) {
                        query.push({
                            key,
                            value: newState.filters[key],
                        })
                    }
                } else if (filterProps.type === 'boolean') {
                    if (newState.filters[key] != null && newState.filters[key] !== '') {
                        query.push({
                            key,
                            value: newState.filters[key],
                        })
                    }
                } else if (filterProps.type === 'custom') {
                    const val = newState.filters[key]
                    if (val && (Array.isArray(val) ? val.length > 0 : true)) {
                        query.push({
                            key,
                            value: Array.isArray(val) ? val.join(',') : val,
                        })
                    }
                }
            }
        }

        // Set search query
        if (newState.query && newState.query.trim()) {
            query.push({
                key: 'query',
                value: newState.query,
            })
        }

        // Set order by
        if (newState.orderBy && newState.orderBy !== defaultSortColumn) {
            query.push({
                key: 'orderBy',
                value: newState.orderBy,
            })
        }

        // Set order by direction
        if (
            newState.orderByDirection &&
            newState.orderByDirection !== defaultSortDirection
        ) {
            query.push({
                key: 'orderByDirection',
                value: newState.orderByDirection,
            })
        }

        // Set columns
        const defaultColumns = availableColumns.filter(
            (column) => column.display === 'optional_default'
        )

        // Set location_id
        if (newState.location_id) {
            query.push({
                key: 'location_id',
                value: newState.location_id,
            })
        }

        const isDefaultColumns =
            JSON.stringify(newState.columns) ===
            JSON.stringify(defaultColumns.map((column) => column.key))

        if (!isDefaultColumns) {
            query.push({
                key: 'columns',
                value: newState.columns.join(','),
            })
        }

        if (newState.columnOrder) query.push({ key: 'columnOrder', value: newState.columnOrder.join(',') })

        const queryString = query
            .map((param) => encodeURIComponent(param.key) + '=' + encodeURIComponent(param.value))
            .join('&')

        const pathname = queryString ? `?${queryString}` : location.pathname

        navigate(pathname)
    }

    // Watch search params changes
    useEffect(() => {
        // Get state from URL
        const page = searchParams.get('page') ? +searchParams.get('page') : 1
        const requestedPerPage = Number(searchParams.get('perPage'))
        const perPage = Number.isInteger(requestedPerPage) && requestedPerPage > 0 ? Math.min(requestedPerPage, 100) : 100

        // Search available filters in URL
        const filters = {}

        for (const availableFilter of availableFilters) {
            if (searchParams.get(availableFilter.key)) {
                const filterType = availableFilter.type

                if (filterType === 'multiselect') {
                    filters[availableFilter.key] =
                        searchParams.get(availableFilter.key).split(',')
                } else if (filterType === 'select') {
                    filters[availableFilter.key] =
                        searchParams.get(availableFilter.key)
                } else if (filterType === 'boolean') {
                    filters[availableFilter.key] =
                        searchParams.get(availableFilter.key) === 'true'
                } else if (filterType === 'custom') {
                    const raw = searchParams.get(availableFilter.key)
                    filters[availableFilter.key] = availableFilter.multiple
                        ? raw.split(',')
                        : raw
                }
            }
        }

        // Find query search
        const query = searchParams.get('query') ?? null

        // Find order by
        const orderBy = searchParams.get('orderBy') ?? defaultSortColumn
        const orderByDirection =
            searchParams.get('orderByDirection') ?? defaultSortDirection

        // Find columns
        let columns = []

        // If no columns in URL, use default columns
        if (!searchParams.has('columns')) {
            for (const column of availableColumns) {
                if (column.display === 'optional_default') {
                    columns.push(column.key)
                }
            }
        } else {
            columns = searchParams.get('columns').split(',')
        }

        const resolvedColumns = resolveTableColumnAliases(columns, orderBy, availableColumns)

        // Location id
        const location_id = searchParams.get('location_id') ?? null

        // Update state
        const newState = {
            viewId: searchParams.get('viewId'),
            filters,
            page,
            perPage,
            query,
            orderBy: resolvedColumns.orderBy,
            orderByDirection,
            columns: resolvedColumns.columns,
            columnOrder: searchParams.has('columnOrder') ? searchParams.get('columnOrder').split(',') : undefined,
            location_id,
        }

        setTableState(newState)
        setQuery(query || '')

        // Fetch data
        fetchData(newState)
    }, [searchParams])

    // Reacting to page change
    const changePage = async (page) => {
        updateURLWithNewState({ ...tableState, page })
    }

    const changePerPage = async (perPage) => {
        updateURLWithNewState({
            ...tableState,
            perPage,
            page: 1,
        })
    }

    // Reacting to column click (sort)
    const columnClick = async (column) => {
        // Don't handle non-sortable columns
        if (!column.sortable) return

        const orderBy = column.key

        const orderByDirection =
            tableState.orderBy === column.key
                ? tableState.orderByDirection === 'asc'
                    ? 'desc'
                    : 'asc'
                : 'asc'

        updateURLWithNewState({
            ...tableState,
            orderBy,
            orderByDirection,
            page: 1,
        })
    }

    // Toggling columns
    const toggleColumn = (column) => {
        if (column.display === 'mandatory') return
        const keys = orderedTableColumns(availableColumns, tableState.columns, tableState.columnOrder).map((item) => item.key)
        const selected = keys.includes(column.key)
        if (selected && keys.length === 1) return
        updateURLWithNewState({
            ...tableState,
            columns: selected ? keys.filter((key) => key !== column.key) : [...keys, column.key],
        })
    }

    const changeColumns = (columns, columnOrder = tableState.columnOrder) => {
        updateURLWithNewState({ ...tableState, columns, columnOrder })
    }

    const resetColumns = () => {
        const defaultColumns = availableColumns
            .filter((column) => column.display === 'optional_default')
            .map((column) => column.key)

        updateURLWithNewState({
            ...tableState,
            columns: defaultColumns,
            columnOrder: undefined,
        })
    }

    // Views
    const selectView = (view, viewId = null) => {
        // Default colmuns
        const defaultColumns = availableColumns
            .filter((column) => column.display === 'optional_default')
            .map((column) => column.key)

        // Default orderBy and orderByDirection
        const orderBy = view?.orderBy ?? defaultSortColumn
        const orderByDirection = view?.orderByDirection ?? defaultSortDirection

        updateURLWithNewState({
            ...tableState,
            viewId: viewId == null ? null : String(viewId),
            filters: view.filters ?? {},
            columns: view?.columns ?? defaultColumns,
            columnOrder: view?.columnOrder,
            orderBy,
            orderByDirection,
            location_id: view?.location_id ?? tableState.location_id,
            page: 1,
        })
    }

    const resetView = () => {
        const defaultColumns = availableColumns
            .filter((column) => column.display === 'optional_default')
            .map((column) => column.key)

        updateURLWithNewState({
            ...tableState,
            viewId: null,
            filters: {},
            orderBy: defaultSortColumn,
            orderByDirection: defaultSortDirection,
            query: null,
            columns: defaultColumns,
            columnOrder: undefined,
            page: 1,
        })
    }

    // Selectable items
    const [selectedItems, setSelectedItems] = useState([])
    const getSelectKey = (item) => item[selectKey]

    const toggleItemSelect = (item) => {
        const id = getSelectKey(item)

        const isSelected = selectedItems.includes(id)

        const newSelectedItems = isSelected
            ? selectedItems.filter((selectedItem) => selectedItem !== id)
            : [...selectedItems, id]

        setSelectedItems(newSelectedItems)
    }

    // Scroll restauration

    // Everytime the table element is scrolled, save the scroll position (debounced)
    const saveScroll = debounce(() => {
        const tableStateHash = md5(
            'table.' + JSON.stringify(tableStateRef.current)
        )

        if (tableRef && tableRef.current) {
            const currentScroll = tableRef.current.scrollTop

            // Get current timestamp in seconds
            const timestamp = Date.now()

            // Get current scroll positions in sessionStorage
            const currentScrollPositions =
                JSON.parse(sessionStorage.getItem('scrollPositions')) || {}

            // Remove old scroll positions (timestamp older than 1 hour)
            Object.keys(currentScrollPositions).forEach((key) => {
                if (
                    timestamp - currentScrollPositions[key].timestamp >
                    3600000
                ) {
                    delete currentScrollPositions[key]
                }
            })

            // Add current scroll position to object
            currentScrollPositions[tableStateHash] = {
                scroll: currentScroll,
                timestamp,
            }

            // Save current scroll position
            sessionStorage.setItem(
                'scrollPositions',
                JSON.stringify(currentScrollPositions)
            )
        }
    }, 100)

    // Keep ref in sync with state
    useEffect(() => {
        tableStateRef.current = tableState
    }, [tableState])

    useEffect(() => {
        const handleScroll = () => {
            saveScroll()
        }

        if (tableRef && tableRef.current) {
            tableRef.current.addEventListener('scroll', handleScroll)
        }

        return () => {
            if (tableRef && tableRef.current) {
                tableRef.current.removeEventListener('scroll', handleScroll)
            }
        }
    }, [])

    const restoreScroll = () => {
        if (tableRef.current) {
            const tableStateHash = md5(
                'table.' + JSON.stringify(tableStateRef.current)
            )

            const currentScrollPositions =
                JSON.parse(sessionStorage.getItem('scrollPositions')) || {}

            const scrollPosition = currentScrollPositions[tableStateHash]

            // Check if scroll position exists and is not older than 1 hour
            if (
                scrollPosition &&
                scrollPosition.timestamp > Date.now() - 3600000
            ) {
                tableRef.current.scrollTo({
                    top: scrollPosition.scroll,
                })

                setTimeout(() => {
                    tableRef.current.scrollTo({
                        top: scrollPosition.scroll,
                    })
                }, 100)
            }
        }
    }

    return {
        tableState,
        tableData,
        count,
        isFetching: isFetching || loading,
        refetchData,
        changePage,
        changePerPage,
        columns,
        changeFilter,
        changeFilters,
        query,
        setQuery,
        columnClick,
        resetFilters,
        toggleColumn,
        resetColumns,
        changeColumns,
        selectView,
        resetView,
        selectedItems,
        toggleItemSelect,
        changeLocationId,
    }
}
